import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Spinner } from "../ui";
import { ListChecks, CircleDot, CheckCircle2, AlertTriangle, Download } from "lucide-react";

function Kpi({ Icon, label, value, tone, testid }) {
  return (
    <div className="rounded-2xl border bg-surface p-4" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-xl mb-3 ${tone}`}><Icon className="w-4.5 h-4.5" /></div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-text-secondary mt-0.5">{label}</div>
    </div>
  );
}

export default function BoardDashboard({ board, members = [] }) {
  const { t } = useApp();
  const [report, setReport] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(`/boards/${board.id}/report`).then(({ data }) => { if (live) setReport(data); }).catch(() => live && setReport({ error: true }));
    return () => { live = false; };
  }, [board.id]);

  if (!report) return <div className="py-16 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>;
  if (report.error) return <div className="py-16 text-center text-sm text-text-secondary">{t("report_error")}</div>;

  const { totals, by_status, by_owner, unassigned_open, trend } = report;
  const maxStatus = Math.max(1, ...by_status.map((s) => s.count));
  const maxTrend = Math.max(1, ...trend.flatMap((w) => [w.created, w.completed]));
  const nameOf = (uid) => { const m = members.find((x) => x.user_id === uid); return m?.name || m?.email || t("report_unknown_user"); };
  const ownerRows = [...by_owner];
  if (unassigned_open) ownerRows.push({ user_id: "__none__", open_count: unassigned_open, _unassigned: true });
  const maxOwner = Math.max(1, ...ownerRows.map((o) => o.open_count));

  const exportCsv = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = [
      ["Metric", "Value"],
      [t("report_total"), totals.total], [t("report_open"), totals.open],
      [t("report_done"), totals.done], [t("report_overdue"), totals.overdue],
      [t("report_completion"), `${totals.completion_pct}%`], [],
      [t("report_by_status"), t("report_total")],
      ...by_status.map((s) => [s.label, s.count]), [],
      [t("report_owner_workload"), t("report_open")],
      ...ownerRows.map((o) => [o._unassigned ? t("report_unassigned") : nameOf(o.user_id), o.open_count]), [],
      [t("report_completion_trend"), t("report_created"), t("report_completed")],
      ...trend.map((w) => [w.week, w.created, w.completed]),
    ];
    const csv = rows.map((r) => r.map(cell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${(board.name || "board").replace(/[^a-z0-9]+/gi, "-")}-report.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="board-dashboard">
      <div className="flex justify-end -mb-2">
        <button onClick={exportCsv} data-testid="board-report-export"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs text-text-secondary hover:text-text-primary transition-colors">
          <Download className="w-3.5 h-3.5" />{t("report_export")}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi Icon={ListChecks} label={t("report_total")} value={totals.total} tone="bg-text-primary/10 text-text-primary" testid="kpi-total" />
        <Kpi Icon={CircleDot} label={t("report_open")} value={totals.open} tone="bg-sky-500/15 text-sky-400" testid="kpi-open" />
        <Kpi Icon={CheckCircle2} label={t("report_done")} value={totals.done} tone="bg-emerald-500/15 text-emerald-400" testid="kpi-done" />
        <Kpi Icon={AlertTriangle} label={t("report_overdue")} value={totals.overdue} tone="bg-red-500/15 text-red-400" testid="kpi-overdue" />
        <Kpi Icon={CheckCircle2} label={t("report_completion")} value={`${totals.completion_pct}%`} tone="bg-violet-500/15 text-violet-400" testid="kpi-completion" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Status breakdown */}
        <div className="rounded-2xl border bg-surface p-5" data-testid="report-status">
          <h3 className="text-sm font-semibold mb-4">{t("report_by_status")}</h3>
          <div className="space-y-3">
            {by_status.map((s) => (
              <div key={s.id} className="flex items-center gap-3">
                <div className="w-24 text-xs text-text-secondary truncate text-right">{s.label}</div>
                <div className="flex-1 h-5 rounded-md bg-surface-hover overflow-hidden">
                  <div className="h-full rounded-md transition-all" style={{ width: `${(s.count / maxStatus) * 100}%`, backgroundColor: s.color, minWidth: s.count ? 6 : 0 }} />
                </div>
                <div className="w-7 text-xs tabular-nums text-right">{s.count}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Owner workload */}
        <div className="rounded-2xl border bg-surface p-5" data-testid="report-owners">
          <h3 className="text-sm font-semibold mb-4">{t("report_owner_workload")}</h3>
          {ownerRows.length === 0 ? (
            <p className="text-xs text-text-secondary">{t("report_no_open")}</p>
          ) : (
            <div className="space-y-3">
              {ownerRows.map((o) => (
                <div key={o.user_id} className="flex items-center gap-3">
                  <div className={`w-28 text-xs truncate text-right ${o._unassigned ? "italic text-text-secondary" : ""}`}>
                    {o._unassigned ? t("report_unassigned") : nameOf(o.user_id)}
                  </div>
                  <div className="flex-1 h-5 rounded-md bg-surface-hover overflow-hidden">
                    <div className="h-full rounded-md bg-sky-400/80 transition-all" style={{ width: `${(o.open_count / maxOwner) * 100}%`, minWidth: 6 }} />
                  </div>
                  <div className="w-7 text-xs tabular-nums text-right">{o.open_count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Completion over time */}
      <div className="rounded-2xl border bg-surface p-5" data-testid="report-trend">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">{t("report_completion_trend")}</h3>
          <div className="flex items-center gap-4 text-[11px] text-text-secondary">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-text-primary/40" />{t("report_created")}</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400" />{t("report_completed")}</span>
          </div>
        </div>
        <div className="flex items-end gap-3 h-40">
          {trend.map((w) => (
            <div key={w.week} className="flex-1 flex flex-col items-center gap-1.5 group">
              <div className="w-full flex items-end justify-center gap-1 h-32">
                <div className="w-1/3 rounded-t bg-text-primary/40 transition-all group-hover:bg-text-primary/60" style={{ height: `${(w.created / maxTrend) * 100}%`, minHeight: w.created ? 4 : 0 }} title={`${t("report_created")}: ${w.created}`} />
                <div className="w-1/3 rounded-t bg-emerald-400 transition-all group-hover:bg-emerald-300" style={{ height: `${(w.completed / maxTrend) * 100}%`, minHeight: w.completed ? 4 : 0 }} title={`${t("report_completed")}: ${w.completed}`} />
              </div>
              <div className="text-[10px] text-text-secondary">{w.week.slice(5)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
